import { UnwrapPromise } from "@prisma/client";
import { LoaderFunction, Outlet, useLoaderData, useMatches } from "remix";
import invariant from "tiny-invariant";
import { BreadcrumbLink } from "~/components/Breadcrumbs";
import { EditButtonLink } from "~/components/Button";
import { ImageSlider } from "~/components/ImageSlider";
import { db } from "~/utils/db.server";
import { Unpacked } from "~/utils/types";
import { useIsLoggedIn } from "~/utils/useIsLoggedIn";

interface LoaderData {
  booth: UnwrapPromise<ReturnType<typeof loadBooth>>;
}

function loadBooth(slug: string) {
  return db.booth.findUnique({
    where: { slug },
    include: { image: true },
  });
}

export const loader: LoaderFunction = async ({
  params,
}): Promise<LoaderData> => {
  invariant(typeof params.slug === "string", "slug is required");

  const booth = await loadBooth(params.slug);

  if (!booth) {
    throw new Response("Not Found", {
      status: 404,
    });
  }

  return {
    booth,
  };
};

export const handle = {
  breadcrumb: ({ pathname, data }: Unpacked<ReturnType<typeof useMatches>>) => {
    return data?.booth?.title ? (
      <BreadcrumbLink to={`${pathname}/${data?.booth?.slug}`}>
        {data?.booth?.title}
      </BreadcrumbLink>
    ) : (
      <>…</>
    );
  },
};

export function CatchBoundary() {
  return (
    <div className="p-5">
      <h2>We couldn't find that booth!</h2>
    </div>
  );
}

export default function BoothSlug() {
  const { booth } = useLoaderData<LoaderData>();

  const isLoggedIn = useIsLoggedIn();

  invariant(booth, "Booth is required");

  return (
    <article className="my-5 max-w-[980px]">
      {!!booth.image && <ImageSlider images={[booth.image]} />}

      <h1 className="text-4xl font-bold">{booth.title}</h1>

      <p className="text-xl my-2 leading-3 text-primary-500">{booth.summary}</p>

      <div
        className="prose lg:prose-xl"
        dangerouslySetInnerHTML={{ __html: booth.text }}
      />

      <Outlet />

      {isLoggedIn && <EditButtonLink to="edit">Edit</EditButtonLink>}
    </article>
  );
}
